import { ChartBarIcon, ExclamationTriangleIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { KnowledgeLink } from '@/components/ui/KnowledgeLink';

interface AggregatedPnlEmptyStateProps {
    /** Whether any collection/tag filters are active */
    hasFilters?: boolean;
    /** Callback to reset filters */
    onResetFilters?: () => void;
    /** Whether the data is currently loading */
    isLoading?: boolean;
    /** Whether all ledgers are excluded (TRACKING_MODE_OTHER) */
    allLedgersExcluded?: boolean;
    /** Number of excluded ledgers */
    excludedCount?: number;
    /** Minimum height to match chart container and prevent layout shift */
    minHeight?: number;
}

/**
 * Empty state component for the aggregated P&L chart.
 * Shows different messages based on:
 * - Loading state
 * - All ledgers excluded (TRACKING_MODE_OTHER)
 * - Filters applied but no data
 * - No data available
 */
export function AggregatedPnlEmptyState({
    hasFilters = false,
    onResetFilters,
    isLoading = false,
    allLedgersExcluded = false,
    excludedCount = 0,
    minHeight,
}: AggregatedPnlEmptyStateProps) {
    const containerStyle = minHeight ? { minHeight } : undefined;

    // Loading state
    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center" style={containerStyle}>
                <div className="animate-pulse">
                    <ChartBarIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                    Loading P&L data...
                </p>
            </div>
        );
    }

    // All ledgers excluded (TRACKING_MODE_OTHER)
    if (allLedgersExcluded) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center" style={containerStyle}>
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                    P&L Not Available
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm">
                    {excludedCount > 0
                        ? `All ${excludedCount} ${excludedCount === 1 ? 'ledger is' : 'ledgers are'} tracked as "Other" and excluded from P&L analysis.`
                        : 'All selected ledgers are tracked as "Other" and excluded from P&L analysis.'}
                </p>
                <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 text-center max-w-sm">
                    P&L is calculated for ledgers with inflow/outflow tracking enabled.
                </p>
                {hasFilters && onResetFilters && (
                    <button
                        onClick={onResetFilters}
                        className="mt-4 px-4 py-2 text-sm font-medium text-accent hover:text-accent/80 transition-colors"
                    >
                        Reset Filters
                    </button>
                )}
            </div>
        );
    }

    // Has filters but no data
    if (hasFilters) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center" style={containerStyle}>
                <AdjustmentsHorizontalIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                    No P&L Data
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm">
                    No P&L data available for the selected filters.
                </p>
                {onResetFilters && (
                    <button
                        onClick={onResetFilters}
                        className="mt-4 px-4 py-2 text-sm font-medium text-accent hover:text-accent/80 transition-colors"
                    >
                        Reset Filters
                    </button>
                )}
            </div>
        );
    }

    // No data available
    return (
        <div className="flex flex-col items-center justify-center py-12 text-center" style={containerStyle}>
            <ChartBarIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No P&L Data Yet
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm">
                Add cash flows to your assets to unlock this view.
            </p>
            <p className="mt-3">
                <KnowledgeLink page="pnl">What is P&L?</KnowledgeLink>
            </p>
        </div>
    );
}
